// TelcoTrack — Úřady: územní overlay (WFS) → návrhy povinností.
// Průnik s vrstvou = podnět k posouzení, ne právní pravda.

import { catalogKeyForDoss, overlayProposalNecessity } from './authority-domain.constants';
import { agendaKindForSubject, catalogKeyForNetwork } from './authority-urady-categories.constants';
import type { AuthorityOverlayContact } from './authority-overlay-identity.constants';
import {
  parseAuthorityOverlayLayerId,
  parseAuthorityOverlayMap,
  type AuthorityOverlayMap,
} from './authority-overlay-map.constants';
import { DTM_TI_SCREENING_LAYERS } from './dtm-ti-networks.constants';

export const AUTHORITY_TERRITORIAL_BUFFER_M_MIN = 5;
export const AUTHORITY_TERRITORIAL_BUFFER_M_MAX = 15;
export const AUTHORITY_TERRITORIAL_BUFFER_M_DEFAULT = 10;

export const AUTHORITY_OVERLAY_LAYER_IDS = [
  'dibavod-voda',
  'dibavod-zaplavy',
  'dibavod-chopav',
  'aopk-chko',
  'aopk-natura',
  'npu-pamatky',
  'npu-uan',
  'cgs-chlu',
  'les-pupfl',
  'rsd-silnice',
  'sus-silnice',
  'szdc-zeleznice',
] as const;
export type AuthorityOverlayLayerId = (typeof AUTHORITY_OVERLAY_LAYER_IDS)[number];

export interface AuthorityOverlayLayerDef {
  layerId: AuthorityOverlayLayerId;
  subjectKey: string;
  labelCs: string;
}

/** Public overlay layers → DOSS subject. Obce a KN vlastníci sem nepatří. */
export const AUTHORITY_OVERLAY_LAYERS: readonly AuthorityOverlayLayerDef[] = [
  { layerId: 'dibavod-voda', subjectKey: 'povodi', labelCs: 'DIBAVOD / vodní útvar' },
  { layerId: 'dibavod-zaplavy', subjectKey: 'povodi', labelCs: 'DIBAVOD / záplavové území' },
  { layerId: 'dibavod-chopav', subjectKey: 'povodi', labelCs: 'DIBAVOD / CHOPAV' },
  { layerId: 'aopk-chko', subjectKey: 'aopk', labelCs: 'AOPK CHKO / CHÚ' },
  { layerId: 'aopk-natura', subjectKey: 'aopk', labelCs: 'AOPK Natura 2000' },
  { layerId: 'npu-pamatky', subjectKey: 'npu', labelCs: 'NPÚ památky' },
  { layerId: 'npu-uan', subjectKey: 'archeologicky_ustav', labelCs: 'NPÚ ÚAN / archeologie' },
  { layerId: 'cgs-chlu', subjectKey: 'cgs_chlu', labelCs: 'ČGS CHLÚ / ložiska' },
  { layerId: 'les-pupfl', subjectKey: 'lesy_cr', labelCs: 'PUPFL / les' },
  { layerId: 'rsd-silnice', subjectKey: 'rsd', labelCs: 'ŘSD / dálnice a silnice I. třídy' },
  { layerId: 'sus-silnice', subjectKey: 'sus', labelCs: 'SÚS / ostatní silnice' },
  { layerId: 'szdc-zeleznice', subjectKey: 'drazni_urad', labelCs: 'Železnice / TN_RAIL' },
] as const;

export interface AuthorityTerritorialProposal {
  subjectKey: string;
  catalogKey?: string;
  municipality?: string;
  reason: string;
  necessity: 'assess';
  layerId: string;
  featureId?: string;
  agendaKind?: 'official' | 'network_crossing' | 'construction_prep';
  /** false = show in dry-run unchecked (DTM review / MoD). */
  defaultSelected?: boolean;
  /** Jen když vrstva / tabulka dá IČO nebo ID DS. Jinak vynecháno. */
  contact?: AuthorityOverlayContact;
  /** Proč chybí Správce — UI místo tichého „—“. Může být i u kontaktu bez DS/e-mailu. */
  contactGap?: string;
  /** Identita z DTM (jméno / IČO) i když DS/e-mail chybí. */
  identifiedSubject?: {
    name: string;
    role?: string;
    ico?: string;
  };
  /** Průnik trasy s vrstvou — jen když WFS/DTM vrátil geometrii. Není v dry-run JSON. */
  overlayMap?: AuthorityOverlayMap;
}

export interface AuthoritySkippedLayer {
  layerId: string;
  reason: string;
}

export interface AuthorityProposeSubjectsResponse {
  proposals: AuthorityTerritorialProposal[];
  skippedLayers: AuthoritySkippedLayer[];
  applied?: number;
}

export function clampTerritorialBufferMeters(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(String(raw ?? '').trim());
  if (!Number.isFinite(n)) return AUTHORITY_TERRITORIAL_BUFFER_M_DEFAULT;
  return Math.min(
    AUTHORITY_TERRITORIAL_BUFFER_M_MAX,
    Math.max(AUTHORITY_TERRITORIAL_BUFFER_M_MIN, Math.round(n)),
  );
}

export function overlayNecessityReason(layerLabel: string, featureLabel?: string | null): string {
  const layer = layerLabel.trim() || 'veřejnou vrstvou';
  const feature = (featureLabel ?? '').trim();
  return feature ? `Průnik s ${layer} (${feature})` : `Průnik s ${layer}`;
}

export function territorialProposalFromOverlay(input: {
  subjectKey: string;
  layerId: string;
  layerLabel: string;
  featureId?: string | null;
  featureLabel?: string | null;
  municipality?: string | null;
  contact?: AuthorityOverlayContact | null;
  contactGap?: string | null;
  identifiedSubject?: { name: string; role?: string; ico?: string } | null;
}): AuthorityTerritorialProposal {
  overlayProposalNecessity(input.subjectKey);
  const agendaKind = agendaKindForSubject(input.subjectKey);
  const proposal: AuthorityTerritorialProposal = {
    subjectKey: input.subjectKey,
    catalogKey:
      agendaKind === 'network_crossing'
        ? catalogKeyForNetwork(input.subjectKey, input.municipality)
        : catalogKeyForDoss(input.subjectKey, input.municipality),
    reason: overlayNecessityReason(input.layerLabel, input.featureLabel),
    necessity: 'assess',
    layerId: input.layerId,
    agendaKind,
    defaultSelected: true,
  };
  if (input.municipality?.trim()) proposal.municipality = input.municipality.trim();
  if (input.featureId?.trim()) proposal.featureId = input.featureId.trim();
  if (input.contact) proposal.contact = input.contact;
  if (input.contactGap?.trim()) proposal.contactGap = input.contactGap.trim();
  const identified = input.identifiedSubject?.name?.trim();
  if (identified) {
    proposal.identifiedSubject = {
      name: identified,
      ...(input.identifiedSubject?.role?.trim() ? { role: input.identifiedSubject.role.trim() } : {}),
      ...(input.identifiedSubject?.ico?.trim() ? { ico: input.identifiedSubject.ico.trim() } : {}),
    };
  }
  return proposal;
}

const RSD_HINT = /\b(dalnic|motorway|freeway|expressway|firstclass|mainroad|primary)/;
const SUS_HINT = /\b(silnice\s+iii?|iii?\.\s*tr|secondclass|thirdclass|secondary|tertiary)/;
const RSD_CLASS_I = /\b(silnice\s+i\.|i\.\s*trid)/;

function foldRoadText(value: string): string {
  return value
    .toLocaleLowerCase('cs')
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

/** Classify INSPIRE / ŘSD road attributes. Unknown road → SÚS (ostatní), not ŘSD. */
export function classifyRoadSubject(properties: Record<string, unknown> | null | undefined): 'rsd' | 'sus' {
  const blob = foldRoadText(
    Object.values(properties ?? {})
      .map((value) => String(value ?? '').trim())
      .filter(Boolean)
      .join(' '),
  );
  if (SUS_HINT.test(blob)) return 'sus';
  if (RSD_HINT.test(blob) || RSD_CLASS_I.test(blob)) return 'rsd';
  return 'sus';
}

export function overlayLayerIdsForSubjectKey(subjectKey: string | null | undefined): AuthorityOverlayLayerId[] {
  const key = (subjectKey ?? '').trim();
  if (!key) return [];
  return AUTHORITY_OVERLAY_LAYERS.filter((row) => row.subjectKey === key).map((row) => row.layerId);
}

/** Layer ids to try for live VYKOP × WFS/DTM resolve (map-overlay, „Zobrazit na mapě“). */
export function overlayLayerIdsForMapResolve(input: {
  subjectKey?: string | null;
  evidencePayload?: unknown;
}): string[] {
  const overlayMap = parseAuthorityOverlayMap(input.evidencePayload ?? null);
  const fromEvidence = parseAuthorityOverlayLayerId(input.evidencePayload ?? null);
  const ids: string[] = [];
  if (overlayMap?.layerId?.trim()) ids.push(overlayMap.layerId.trim());
  if (fromEvidence) ids.push(fromEvidence);
  ids.push(...overlayLayerIdsForSubjectKey(input.subjectKey));
  const subject = (input.subjectKey ?? '').trim();
  if (subject) {
    for (const row of DTM_TI_SCREENING_LAYERS) {
      if (row.subjectKey === subject) ids.push(`dtm-${row.wmsLayer}`);
    }
  }
  return [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
}

export function overlayLayerDef(layerId: string): AuthorityOverlayLayerDef | undefined {
  return AUTHORITY_OVERLAY_LAYERS.find((row) => row.layerId === layerId);
}
